import * as React from 'react';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

/** Material UI Packages. */
import { Autocomplete, Box, Button, Popper, TextField, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GridActionsCellItem, gridFocusCellSelector, useGridApiContext, useGridApiRef } from '@mui/x-data-grid-pro';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import {
    addRateManagementData,
    updateRateManagementData,
    deleteRateManagementData,
} from 'shared/store/slices/rateManagementSlice';

/** Internal Components. */
import { handleTabKeyDown } from 'shared/ui/Datagrid';
import { customized_style } from 'shared/config/Styles';

import { getRateManagementRateContext } from '../lib/rateManagementShared';
import MultilineNoteEditCell, { preventNoteEnterLineBreak } from 'shared/ui/MultilineNoteEditCell';
import { splitClipboardPastedText, makeQuotedClipboardCopyHandler } from 'shared/lib/gridClipboard';
import { createSocketData, readSocketData, deleteSocketData, handleProcessRowUpdate } from 'shared/lib/fetchSocket';

/** 편집 가능한 il_rate 필드. 그리드 행이 곧 il_rate 행이므로 필드명이 그대로 와이어 키다. */
const EDITABLE_FIELDS = ['il_rate_type_id', 'rate', 'note'];

/** 실제 렌더 높이. `density` 를 걸지 않아 배율 없이 이 값 그대로 그려진다(편집 전후 동일, 예전 compact 28×0.7 과 같은 19px). */
const ROW_HEIGHT = 19;
const HEADER_HEIGHT = 21;
const borderColor = 'rgba(224, 224, 224, 1)';
const headerDividerColor = '#c8c8c8';

/** 임시(로컬 전용) 행은 `randomId()` 문자열 id, 서버에 저장된 행은 숫자 PK. */
const isUnsaved = (id) => typeof id === 'string';

const addBtnBaseSx = {
    fontSize: '11px',
    textTransform: 'none',
    py: 0.25,
    px: 0.75,
    minWidth: 0,
    lineHeight: 1.2,
};

/** Rate: 소수는 허용하지만 음수·문자는 불가 — 붙여넣기도 이 `valueParser`를 그대로 재사용한다. */
function parseNonNegativeDecimal(raw) {
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!/^\d+(\.\d+)?$/.test(s)) return null;
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
}

/** Type: 셀에는 name이 아닌 option.id(정수 FK)를 저장한다 — 목록에 없는 값은 입력/붙여넣기 모두 거부하고 기존 값을 유지. */
function makeRateTypeValueParser(rateType, rateTypeLookupList) {
    return (value, row) => {
        if (value == null || value === '') return null;
        const candidates = [...rateTypeLookupList, ...rateType];
        const match =
            candidates.find((o) => String(o.id) === String(value)) ?? candidates.find((o) => o.name === value);
        return match ? match.id : row.il_rate_type_id;
    };
}

const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': { fontSize: '11px', maxHeight: '200px' },
    '& .MuiAutocomplete-option': { padding: '5px 5px' },
});

/**
 * Type 셀 — 표시/선택은 name, 저장·전송(`il_rate_type_id`)은 option.id(정수 FK)를 사용한다.
 * 입력 텍스트는 직접 관리한다: 글자를 칠 때마다 서버에서 옵션을 다시 받아와 option 객체 참조가 바뀌는데,
 * Autocomplete 에 맡기면 그때마다 입력 텍스트를 선택값 라벨로 되돌려 Backspace 로 지운 글자가 다시 채워진다.
 * 텍스트를 전부 지우면 Type 을 비운 것(null)으로 저장한다.
 */
function RateTypeAutocompleteCell({ params, options, typeLabel, onSearch }) {
    const apiRef = useGridApiContext();
    const { id, field, value } = params;
    const [inputValue, setInputValue] = useState(() => typeLabel(value));

    const currentOption = options.find((o) => String(o.id) === String(value)) ?? null;

    const handleChange = (_event, newValue) => {
        apiRef.current.setEditCellValue({ id, field, value: newValue?.id ?? null });
        setInputValue(newValue?.name ?? '');
    };
    const handleInputChange = (event, text, reason) => {
        if (reason !== 'input') return; // 'reset'(선택값 라벨 복원) 은 무시 — 사용자가 친 글자를 지켜야 한다.
        setInputValue(text);
        if (text === '') apiRef.current.setEditCellValue({ id, field, value: null });
        onSearch(event, text);
    };

    return (
        <Autocomplete
            value={currentOption}
            inputValue={inputValue}
            options={options}
            getOptionLabel={(option) => option?.name ?? ''}
            isOptionEqualToValue={(option, val) =>
                String(option.id) === String(typeof val === 'object' ? val?.id : val)
            }
            onChange={handleChange}
            onInputChange={handleInputChange}
            disableClearable
            fullWidth
            renderInput={(inputParams) => (
                <TextField
                    {...inputParams}
                    variant="standard"
                    autoFocus
                    sx={{ px: 1, '& .MuiInputBase-root': { border: 'none' } }}
                />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ margin: 0, padding: 0 }}>
                    {option.name}
                </li>
            )}
            slotProps={{ popper: { component: StyledPopper } }}
            sx={{
                height: '100%',
                width: '100%',
                '& .MuiInputBase-input': { fontSize: '11px' },
                '& .MuiInputBase-root': { p: 0, height: '100%' },
                '& .MuiFormControl-root': { height: '100%' },
            }}
        />
    );
}

/**
 * Note 셀의 글자·여백 값. 보기 상태와 편집 상태가 이 한 벌을 똑같이 써야
 * 편집에 들어가고 나올 때 행 높이가 변하지 않는다(행이 `auto` 높이라 차이가 바로 보인다).
 */
const NOTE_METRICS = { fontSize: '11px', lineHeight: 1.4, padY: '2px', padX: 1 };

/** Rate Management 상세 그리드의 Note 편집 셀 스타일. 안쪽 여백은 `sideGridSx` 가 준다. */
const NOTE_EDIT_CELL_SX = {
    '& .MuiInputBase-root': { fontSize: NOTE_METRICS.fontSize },
    '& .MuiInputBase-input': { fontSize: NOTE_METRICS.fontSize, lineHeight: NOTE_METRICS.lineHeight },
};

/**
 * Note 표시 셀 — 줄바꿈은 그대로, 긴 한 줄은 셀 폭에서 자동으로 줄을 바꿔 전부 보여준다(행은 auto 높이로 늘어남).
 * 세로 가운데 정렬은 셀(컬럼 `display: 'flex'`, align-items: center)에 맡기므로 행 높이만큼의 line-height 를 주지 않는다
 * (행 높이와 같은 line-height 는 1px 테두리를 가진 셀을 넘쳐 행 구분선이 어긋나 보였다).
 */
const renderNoteCell = (params) => (
    <Typography
        sx={{
            ...customized_style.itemDatagrid_Typography,
            display: 'block',
            textAlign: 'left',
            width: '100%',
            height: 'auto',
            fontSize: NOTE_METRICS.fontSize,
            lineHeight: NOTE_METRICS.lineHeight,
            py: NOTE_METRICS.padY,
            px: NOTE_METRICS.padX,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        }}
    >
        {params.value ?? ''}
    </Typography>
);

/** 모든 행을 auto 높이로 둔다 — Note 가 줄바꿈되면 늘어나고, 최소 높이는 sx 의 `row--dynamicHeight` 규칙이 `ROW_HEIGHT` 로 지킨다. */
const getRowHeight = () => 'auto';

/** 행이 없을 때 그리드가 기본 "No rows" 오버레이 높이(300px)로 커지지 않게 빈 오버레이를 둔다. */
const EmptyOverlay = () => null;

/**
 * 내장 그리드 스타일. 편집 셀의 input 은 셀 높이를 100% 채워 View ↔ Edit 전환 때 셀이 작아 보이지 않게 한다
 * (`rateManagementDataGridFontSx` 의 `.MuiDataGrid-detailPanel .MuiInputBase-*` 보다 선택자를 길게 둬 우선 적용).
 * Note 편집 셀은 예외로 textarea 가 autosize 로 늘어나면 행도 같이 늘어난다.
 */
const sideGridSx = {
    backgroundColor: '#eeeeee',
    '--DataGrid-overlayHeight': `${ROW_HEIGHT}px`,
    '& .MuiDataGrid-cell': { py: 0, alignItems: 'center' },
    '& .MuiDataGrid-columnHeader': { py: 0, backgroundColor: '#dfdfdf' },
    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#dfdfdf' },
    '& .MuiDataGrid-columnGroupsRow': { width: '100%' },
    '& .MuiDataGrid-columnHeader--withRightBorder:not(.MuiDataGrid-columnHeader--filledGroup):not(.MuiDataGrid-columnHeader--last)':
        { borderRightColor: headerDividerColor },
    '& .MuiDataGrid-columnHeaders [role="row"] + [role="row"]': { borderTop: `1px solid ${headerDividerColor}` },
    '&& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': { borderBottom: 'none' },
    '& .MuiDataGrid-columnHeaderTitleContainerContent': { justifyContent: 'center' },
    '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#dfdfdf' },
    '& .MuiDataGrid-virtualScroller': { backgroundColor: '#eeeeee' },
    '& .MuiDataGrid-row': { backgroundColor: '#eeeeee' },
    '& .rate-cell--unsaved': { opacity: 0.45 },
    '& .MuiDataGrid-actionsCell .MuiIconButton-root': { p: '1px' },
    '& .MuiDataGrid-actionsCell .MuiSvgIcon-root': { fontSize: 16 },
    '& .MuiDataGrid-row--dynamicHeight .MuiDataGrid-cell': { minHeight: ROW_HEIGHT },
    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': { p: 0, alignItems: 'stretch', boxShadow: 'none' },
    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing .MuiInputBase-root': {
        height: '100%',
        minHeight: 0,
        p: 0,
        fontSize: '11px',
    },
    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing .MuiInputBase-input': {
        height: '100%',
        boxSizing: 'border-box',
        py: 0,
        px: 1,
        fontSize: '11px',
        textAlign: 'center',
    },
    // Note 편집 셀은 보기 상태(`renderNoteCell`)와 같은 글자·여백을 쓰고, 높이는 textarea 의 자동 늘어남에 맡긴다.
    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing[data-field="note"]': { py: NOTE_METRICS.padY },
    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing[data-field="note"] .MuiInputBase-root': {
        alignItems: 'flex-start',
        height: 'auto',
    },
    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing[data-field="note"] .MuiInputBase-input': {
        textAlign: 'left',
        height: 'auto',
        py: 0,
        px: NOTE_METRICS.padX,
        fontSize: NOTE_METRICS.fontSize,
        lineHeight: NOTE_METRICS.lineHeight,
    },
};

/**
 * NET 또는 SELLING 한 쪽의 독립 그리드. 행은 il_rate 행 그대로(정렬: 저장된 행 id 오름차순, 임시 행은 추가 순서대로 맨 아래).
 * Shipment Rate Info 다이얼로그(`UpdateRateInfoTable`)와 같은 구조라 NET/SELLING 사이에 index 짝 맞춤이 없다.
 */
function RateSideGrid({
    category,
    rows,
    rateType,
    rateTypeLookupList,
    typeLabel,
    onRateTypeInput,
    onDelete,
    onProcessRowUpdate,
    pendingFocusId,
    onPendingFocusHandled,
}) {
    const apiRef = useGridApiRef();

    /** 임시 행이 그리드에 렌더된 뒤 Type 셀을 편집 모드로 자동 포커스한다. */
    useEffect(() => {
        if (pendingFocusId == null || !rows.some((r) => r.id === pendingFocusId)) return undefined;
        onPendingFocusHandled();
        const raf = requestAnimationFrame(() => {
            try {
                apiRef.current?.startCellEditMode({ id: pendingFocusId, field: 'il_rate_type_id' });
            } catch (error) {
                // Row not mounted yet — ignore, user can click the cell to edit.
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [rows, pendingFocusId, onPendingFocusHandled, apiRef]);

    /** 임시 행이 서버 행으로 바뀌면 포커스가 있던 셀(임시 id)이 사라져 포커스가 그리드 밖으로 빠진다 → 새 행의 같은 열로 되돌린다. */
    const [refocus, setRefocus] = useState(null);
    const processRowUpdate = useCallback(
        (newRow, oldRow) => {
            const field = gridFocusCellSelector(apiRef)?.field;
            return Promise.resolve(onProcessRowUpdate(category, newRow, oldRow)).then((saved) => {
                if (saved && saved.id !== newRow.id && field) setRefocus({ id: saved.id, field });
                return saved;
            });
        },
        [apiRef, onProcessRowUpdate, category],
    );

    useEffect(() => {
        if (!refocus || !rows.some((r) => r.id === refocus.id)) return undefined;
        setRefocus(null);
        const raf = requestAnimationFrame(() => {
            try {
                apiRef.current?.setCellFocus(refocus.id, refocus.field);
                apiRef.current?.setCellSelectionModel?.({ [refocus.id]: { [refocus.field]: true } });
            } catch (error) {
                // Row not mounted yet — ignore.
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [rows, refocus, apiRef]);

    const columns = useMemo(
        () => [
            {
                field: 'il_rate_type_id',
                headerName: 'Type',
                align: 'center',
                headerAlign: 'center',
                editable: true,
                flex: 0.7,
                valueParser: makeRateTypeValueParser(rateType, rateTypeLookupList),
                renderCell: (params) => (
                    <Typography sx={customized_style.itemDatagrid_Typography}>{typeLabel(params.value)}</Typography>
                ),
                renderEditCell: (params) => (
                    <RateTypeAutocompleteCell
                        params={params}
                        options={rateType}
                        typeLabel={typeLabel}
                        onSearch={onRateTypeInput}
                    />
                ),
            },
            {
                field: 'rate',
                headerName: 'Rate',
                align: 'center',
                headerAlign: 'center',
                type: 'number',
                flex: 0.4,
                editable: true,
                valueParser: (v) => parseNonNegativeDecimal(v),
                renderCell: (params) => (
                    <Typography sx={customized_style.itemDatagrid_Typography}>
                        {params.value ? `$${Number(params.value).toLocaleString()}` : ''}
                    </Typography>
                ),
            },
            {
                field: 'note',
                headerName: 'Note',
                align: 'left',
                headerAlign: 'center',
                flex: 1,
                display: 'flex',
                editable: true,
                renderCell: renderNoteCell,
                renderEditCell: (params) => <MultilineNoteEditCell params={params} sx={NOTE_EDIT_CELL_SX} />,
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 36,
                getActions: ({ id }) => [
                    <Tooltip title="Delete" key="del">
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label={`Delete ${category}`}
                            onClick={() => onDelete(id)}
                            color="inherit"
                        />
                    </Tooltip>,
                ],
            },
        ],
        [category, rateType, rateTypeLookupList, typeLabel, onRateTypeInput, onDelete],
    );

    /** 그룹 헤더 한 줄(NET / SELLING)로 구역을 표시한다 — Internal Note 패널 헤더와 높이를 맞추는 기준. */
    const columnGroupingModel = useMemo(
        () => [
            {
                groupId: category,
                headerName: category,
                headerAlign: 'center',
                children: [{ field: 'il_rate_type_id' }, { field: 'rate' }, { field: 'note' }, { field: 'actions' }],
            },
        ],
        [category],
    );

    const handleClipboardCopy = useMemo(() => makeQuotedClipboardCopyHandler(apiRef), [apiRef]);

    /** 셀 키 입력: Note 를 Enter 로 열 때 줄바꿈이 끼어드는 것을 막고, 나머지는 기존 Tab 이동 처리에 넘긴다. */
    const handleCellKeyDown = useCallback(
        (params, event) => {
            preventNoteEnterLineBreak(params, event);
            handleTabKeyDown(apiRef)?.(params, event);
        },
        [apiRef],
    );

    return (
        <DataGridPremium
            rows={rows}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            editMode="cell"
            cellSelection
            rowSelection={false}
            autoHeight
            rowHeight={ROW_HEIGHT}
            columnHeaderHeight={HEADER_HEIGHT}
            getRowHeight={getRowHeight}
            splitClipboardPastedText={splitClipboardPastedText}
            onClipboardCopy={handleClipboardCopy}
            getCellClassName={(params) => (isUnsaved(params.row.id) ? 'rate-cell--unsaved' : '')}
            slots={{ noRowsOverlay: EmptyOverlay }}
            showCellVerticalBorder
            showColumnVerticalBorder
            disableColumnMenu
            disableColumnSorting
            hideFooter
            processRowUpdate={processRowUpdate}
            sx={sideGridSx}
            apiRef={apiRef}
            onCellKeyDown={handleCellKeyDown}
        />
    );
}

/** 저장된 행은 id 오름차순, 임시 행은 추가된 순서대로 맨 아래(안정 정렬). */
const byIdUnsavedLast = (a, b) => {
    const aUnsaved = isUnsaved(a.id);
    const bUnsaved = isUnsaved(b.id);
    if (aUnsaved && bUnsaved) return 0;
    if (aUnsaved) return 1;
    if (bUnsaved) return -1;
    return a.id - b.id;
};

/**
 * Rate Management 상세 패널: NET 그리드 | SELLING 그리드 | Internal Note.
 * 두 그리드는 Shipment Rate Info 다이얼로그처럼 서로 독립이다(행 짝 맞춤 없음).
 */
export default function RateManagementDetailPanel({ row }) {
    const dispatch = useDispatch();

    const rateCtx = getRateManagementRateContext(row);
    const rateData = useSelector((state) => state.rateManagementData.find((r) => r.id === row.id)?.il_rate ?? []);

    const [rateType, setRateType] = useState([]);
    /** 검색으로 좁혀진 `rateType`에 없어도 기존 행의 id→name 라벨을 계속 표시하기 위한 누적 목록. */
    const [rateTypeLookupList, setRateTypeLookupList] = useState([]);
    /** NET/SELLING 클릭 직후, 로컬로만 추가된 임시 행의 Type 셀에 자동으로 편집 포커스를 주기 위한 대기열. */
    const [pendingFocusId, setPendingFocusId] = useState(null);
    const clearPendingFocus = useCallback(() => setPendingFocusId(null), []);
    /** 임시 행의 in-flight create Promise(tempRowId → 생성된 row | null). ack 도착 전 다른 필드가
     * 연달아 커밋되면 재생성 대신 이 Promise 뒤에 update로 이어붙인다(값 유실 방지). */
    const pendingCreates = useRef(new Map());

    const netRows = useMemo(() => rateData.filter((item) => item.category === 'NET').sort(byIdUnsavedLast), [rateData]);
    const sellingRows = useMemo(
        () => rateData.filter((item) => item.category === 'SELLING').sort(byIdUnsavedLast),
        [rateData],
    );

    /** 언마운트 시(검색 결과가 바뀌어 이 행이 사라지는 등) 아직 저장되지 않은 임시 행을 정리한다. */
    const cleanupRef = useRef(null);
    cleanupRef.current = () => {
        rateData.forEach((r) => {
            if (isUnsaved(r.id)) dispatch(deleteRateManagementData(r.id));
        });
    };
    useEffect(() => () => cleanupRef.current?.(), []);

    const mergeRateTypesIntoLookup = useCallback((list) => {
        if (!list?.length) return;
        setRateTypeLookupList((prev) => {
            const m = new Map(prev.map((t) => [t.id, t]));
            list.forEach((t) => {
                if (t?.id != null) m.set(t.id, t);
            });
            return Array.from(m.values());
        });
    }, []);

    /** 디테일 패널이 열리는 시점(= Type 셀이 활성화될 수 있는 가장 이른 시점)에 옵션 목록을 미리 받아둔다. */
    useEffect(() => {
        const tab = rateCtx?.socketTab ?? 0;
        readSocketData(dispatch, tab, 'cell', 'il_rate_type', '', '', (receivedData) =>
            setRateType(receivedData || []),
        );
    }, [dispatch, rateCtx?.socketTab]);

    /** 비활성 포함 전체 목록 — 기존 저장된 행의 id→name 라벨 해석용. */
    useEffect(() => {
        const tab = rateCtx?.socketTab ?? 0;
        readSocketData(dispatch, tab, 'row', 'il_rate_type', '', '', (receivedData) =>
            mergeRateTypesIntoLookup(receivedData || []),
        );
    }, [dispatch, rateCtx?.socketTab, mergeRateTypesIntoLookup]);

    const handleRateTypeInput = useCallback(
        (_event, value) => {
            const tab = rateCtx?.socketTab ?? 0;
            readSocketData(dispatch, tab, 'cell', 'il_rate_type', '', value, (receivedData) => {
                const list = receivedData || [];
                mergeRateTypesIntoLookup(list);
                setRateType(list);
            });
        },
        [dispatch, rateCtx, mergeRateTypesIntoLookup],
    );

    /** NET/SELLING 버튼 클릭 시 서버 호출 없이 로컬 전용 임시 행만 추가한다. 저장은 Type 셀 편집을 커밋할 때 일어난다. */
    const handleAdd = useCallback(
        (category) => {
            if (!rateCtx) return;
            const id = randomId();
            dispatch(
                addRateManagementData({
                    id,
                    [rateCtx.rateKeyName]: rateCtx.parentDbId,
                    il_rate_type_id: null,
                    rate: '',
                    note: '',
                    category,
                }),
            );
            setPendingFocusId(id);
        },
        [dispatch, rateCtx],
    );
    const handleDelete = useCallback(
        (rowId) => {
            if (!rateCtx) return;
            if (isUnsaved(rowId)) {
                dispatch(deleteRateManagementData(rowId));
                return;
            }
            if (window.confirm('Are you sure you want to delete the entry?'))
                // update 와 같은 이유로(팀 room 밖이면 delete broadcast 를 못 받는다) 성공 시 스토어에서 직접 지운다.
                deleteSocketData(dispatch, rateCtx.socketTab, 'row', 'il_rate', 'deleteRateManagementData', rowId, () =>
                    dispatch(deleteRateManagementData(rowId)),
                );
        },
        [dispatch, rateCtx],
    );

    /** 편집된 행을 실제 il_rate 행 생성(미저장 임시 행) 또는 업데이트(기존 행)로 변환. 반환값이 그리드에 반영된다. */
    const saveRow = useCallback(
        (category, newRow, oldRow) => {
            if (!rateCtx) return Promise.resolve(newRow);
            const changedFields = EDITABLE_FIELDS.filter((f) => newRow[f] !== oldRow[f]);
            if (!changedFields.length) return Promise.resolve(newRow);

            const applyAsUpdate = (oldIlRateRow) =>
                Promise.all(
                    changedFields.map((key) =>
                        handleProcessRowUpdate({
                            dispatch,
                            tabValue: rateCtx.socketTab,
                            methodTableName: 'il_rate',
                            reducerName: 'updateRateManagementData',
                            tableId: [],
                            formData: [oldIlRateRow],
                            newRow: { ...oldIlRateRow, [key]: newRow[key] },
                            notAllowNullKey: [],
                            dateField: [],
                        }).then((savedRow) => {
                            // 팀 room 밖(매니저 화면)이면 update broadcast 를 못 받으므로 ack 값으로 스토어를 직접 맞춘다.
                            if (savedRow && key in savedRow) {
                                dispatch(
                                    updateRateManagementData({
                                        id: oldIlRateRow.id,
                                        newData: { [key]: savedRow[key] },
                                    }),
                                );
                            }
                        }),
                    ),
                );

            const rowId = newRow.id;

            if (isUnsaved(rowId)) {
                const pending = pendingCreates.current.get(rowId);
                if (pending) {
                    return pending.then((created) =>
                        created ? applyAsUpdate(created).then(() => ({ ...newRow, id: created.id })) : newRow,
                    );
                }

                // 입력한 값은 임시 행(스토어)에 보관한다 — Type 이 없어 생성을 미루거나 생성이 실패해도 초안이 남아 다시 편집하면 이어진다.
                dispatch(
                    updateRateManagementData({
                        id: rowId,
                        newData: Object.fromEntries(changedFields.map((f) => [f, newRow[f] ?? ''])),
                    }),
                );
                if (!newRow.il_rate_type_id) {
                    dispatch(setSnackbar({ open: true, message: 'Please select a rate type.', severity: 'warning' }));
                    return Promise.resolve(newRow);
                }

                const createPromise = new Promise((resolve) => {
                    createSocketData(
                        dispatch,
                        rateCtx.socketTab,
                        'row',
                        'il_rate',
                        'addRateManagementData',
                        {
                            [rateCtx.rateKeyName]: rateCtx.parentDbId,
                            il_rate_type_id: newRow.il_rate_type_id,
                            rate: newRow.rate,
                            note: newRow.note || '',
                            category,
                        },
                        (response) => {
                            const created = response?.data;
                            dispatch(deleteRateManagementData(rowId));
                            // broadcast 가 ack 보다 먼저 와 이미 들어와 있으면 add 는 무시된다(id 중복).
                            if (created?.id != null) dispatch(addRateManagementData(created));
                            resolve(created ?? null);
                        },
                        // 실패: 임시 행과 초안은 그대로 두어 사용자가 다시 시도할 수 있게 한다.
                        () => resolve(null),
                    );
                }).finally(() => pendingCreates.current.delete(rowId));
                pendingCreates.current.set(rowId, createPromise);
                // 서버 행(진짜 id)으로 resolve 한다 — 임시 id 행으로 resolve 하면 스토어에서 이미 사라진 임시 행이
                // 그리드 캐시에 다시 들어가 유령 행이 남는다.
                return createPromise.then((created) => created ?? newRow);
            }

            const oldIlRateRow = rateData.find((r) => r.id === rowId);
            if (!oldIlRateRow) return Promise.resolve(newRow);
            return applyAsUpdate(oldIlRateRow).then(() => newRow);
        },
        [dispatch, rateCtx, rateData],
    );

    const typesForLabel = rateTypeLookupList.length ? rateTypeLookupList : rateType;
    const typeLabel = useCallback(
        (raw) => {
            if (raw == null || raw === '') return '';
            return typesForLabel.find((o) => String(o.id) === String(raw))?.name ?? '';
        },
        [typesForLabel],
    );

    const sideGridProps = {
        rateType,
        rateTypeLookupList,
        typeLabel,
        onRateTypeInput: handleRateTypeInput,
        onDelete: handleDelete,
        onProcessRowUpdate: saveRow,
        pendingFocusId,
        onPendingFocusHandled: clearPendingFocus,
    };

    return (
        <Box
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                m: 0,
                px: 2,
                py: 1,
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
                bgcolor: '#f1f1f1',
                fontSize: '11px',
                '& .MuiDataGrid-root': {
                    borderRadius: 0,
                    boxShadow: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-main': { borderRadius: 0, boxShadow: 'none' },
            }}
        >
            {/* +NET / +SELLING 툴바 */}
            <Box sx={{ display: 'flex', gap: 0.5, pb: 0.7 }}>
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<AddIcon sx={{ fontSize: '13px !important' }} />}
                    disabled={rateCtx == null}
                    onClick={() => handleAdd('NET')}
                    sx={addBtnBaseSx}
                >
                    NET
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<AddIcon sx={{ fontSize: '13px !important' }} />}
                    disabled={rateCtx == null}
                    onClick={() => handleAdd('SELLING')}
                    sx={{ ...addBtnBaseSx, bgcolor: '#00647e', '&:hover': { bgcolor: '#004d63' } }}
                >
                    SELLING
                </Button>
            </Box>

            {/* NET 그리드 | SELLING 그리드 | Internal Note 패널 — 세 칸은 stretch 로 가장 긴 쪽 높이에 맞춘다 */}
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        borderLeft: `1px solid ${borderColor}`,
                        borderTopLeftRadius: '12px',
                        borderBottomLeftRadius: '12px',
                        overflow: 'hidden',
                        bgcolor: '#eeeeee',
                    }}
                >
                    <RateSideGrid category="NET" rows={netRows} {...sideGridProps} />
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        borderLeft: `1px solid ${headerDividerColor}`,
                        overflow: 'hidden',
                        bgcolor: '#eeeeee',
                    }}
                >
                    <RateSideGrid category="SELLING" rows={sellingRows} {...sideGridProps} />
                </Box>

                {/* Internal Note 패널 — 헤더 1개(그룹 헤더 높이) + 본문 */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        border: `1px solid ${borderColor}`,
                        borderLeft: 'none',
                        borderTopRightRadius: '12px',
                        borderBottomRightRadius: '12px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            height: HEADER_HEIGHT,
                            minHeight: HEADER_HEIGHT,
                            maxHeight: HEADER_HEIGHT,
                            width: '100%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            borderBottom: `1px solid ${borderColor}`,
                            borderLeft: `1px solid ${headerDividerColor}`,
                            bgcolor: '#dfdfdf',
                            fontWeight: 'bold',
                            fontSize: '11px',
                        }}
                    >
                        INTERNAL NOTE
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            px: 1,
                            py: 1,
                            bgcolor: '#eeeeee',
                            borderLeft: `1px solid ${borderColor}`,
                        }}
                    >
                        <Typography
                            sx={{ fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.35 }}
                        >
                            {row?.internal_note ?? ''}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
